import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useDebounce } from "../utils/hooks/useDebounce";
import { getListReprintAuth } from "../services";
import { ReprintAuthRow } from "../types/reprintAuth.type";
import ReprintAuthFilter from "../components/ReprintAuth/Filter";
import ReprintAuthTable from "../components/ReprintAuth/Table";
import SummaryCards, {
  SummaryCardKey,
} from "../components/ReprintAuth/SummaryCards";
import {
  RiskView,
  CHARCOAL,
  buildCreatedByOptions,
  buildRouteOptions,
} from "../components/ReprintAuth/data";

const SAMPLE_LIMIT = 100;

export default function ReprintAuthPage() {
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<string>("all");
  const [routeCode, setRouteCode] = useState<string>("all");
  const [createdBy, setCreatedBy] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [riskView, setRiskView] = useState<RiskView>("all");
  const [triggerFetch, setTriggerFetch] = useState<number>(0);
  const [updatedAt, setUpdatedAt] = useState<string>("");

  // Active-grant sample: powers the summary cards and the derived
  // route/created-by filter options (no dedicated summary/lookup endpoints).
  const [activeSample, setActiveSample] = useState<ReprintAuthRow[]>([]);
  const [activeCount, setActiveCount] = useState<number>(0);

  const debouncedSearch = useDebounce(search, 500);

  const refresh = useCallback(() => setTriggerFetch((prev) => prev + 1), []);

  const fetchSample = useCallback(async () => {
    const {
      data: { data },
    } = await getListReprintAuth({
      page: 1,
      limit: SAMPLE_LIMIT,
      status: "active",
    });
    setActiveSample((data.rows ?? []) as ReprintAuthRow[]);
    setActiveCount(data.count ?? 0);
  }, []);

  useEffect(() => {
    fetchSample();
  }, [fetchSample, triggerFetch]);

  const routeOptions = useMemo(
    () => buildRouteOptions(activeSample),
    [activeSample],
  );
  const createdByOptions = useMemo(
    () => buildCreatedByOptions(activeSample),
    [activeSample],
  );

  const handleSelectCard = (key: SummaryCardKey) => {
    setStatus("active");
    if (key === "near_expiry") {
      setRiskView("near_expiry");
    } else if (key === "over_print") {
      setRiskView("over_print");
    } else if (key === "today") {
      setRiskView("all");
      setDateFrom(dayjs());
      setDateTo(dayjs());
    } else {
      setRiskView("all");
    }
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" sx={{ color: CHARCOAL, fontWeight: 700 }}>
          กำกับสิทธิ์พิมพ์ซ้ำ (Reprint Authorization)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ตรวจสอบและกำกับสิทธิ์การพิมพ์บิลซ้ำของพนักงานขาย
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        การเปิดสิทธิ์ใหม่ทำจากหน้า "รายการขาย"
      </Alert>

      <SummaryCards
        activeSample={activeSample}
        activeCount={activeCount}
        onSelect={handleSelectCard}
      />

      <Paper sx={{ padding: "24px 16px 8px", mb: 2 }}>
        <ReprintAuthFilter
          search={search}
          setSearch={setSearch}
          status={status}
          setStatus={setStatus}
          routeCode={routeCode}
          setRouteCode={setRouteCode}
          createdBy={createdBy}
          setCreatedBy={setCreatedBy}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          riskView={riskView}
          setRiskView={setRiskView}
          routeOptions={routeOptions}
          createdByOptions={createdByOptions}
        />
      </Paper>

      <ReprintAuthTable
        search={debouncedSearch}
        status={status}
        routeCode={routeCode}
        createdBy={createdBy}
        dateFrom={dateFrom}
        dateTo={dateTo}
        riskView={riskView}
        triggerFetch={triggerFetch}
        onRefresh={refresh}
        updatedAt={updatedAt}
        setUpdatedAt={setUpdatedAt}
      />
    </Box>
  );
}
